/**
 * S3.5 - Page /cee/parametres (admin only).
 * Compose 3 sous-sections : mandataires CRUD, templates docx, ingest status.
 */

import MandatairesAdmin from './components/MandatairesAdmin.jsx';
import TemplateAdmin from './components/TemplateAdmin.jsx';
import IngestAdmin from './components/IngestAdmin.jsx';
import { useEffect, useState } from 'react';
import * as ceeApi from '../api/cee.js';

const ROLE_LABELS = {
  admin: 'Admin',
  coordinator: 'Coordinateur',
  driver: 'Livreur',
  partner: 'Partenaire',
};

const ROLES = ['admin', 'coordinator', 'driver', 'partner'];

/** @returns {JSX.Element} */
export default function CeeParametresPage() {
  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-[#0f172a]">Parametres CEE TRA-EQ-131</h1>
        <p className="text-sm text-gray-600 mt-1">
          Configuration administrateur : mandataires, templates docx, etat ingest.
        </p>
      </header>

      <CampaignUsersAdmin />
      <MandatairesAdmin />
      <TemplateAdmin />
      <IngestAdmin />
    </div>
  );
}

function CampaignUsersAdmin() {
  const [users, setUsers] = useState([]);
  const [mandataires, setMandataires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [u, m] = await Promise.all([
        ceeApi.listParametresUsers(),
        ceeApi.listMandataires(),
      ]);
      setUsers(u);
      setMandataires(m);
    } catch (err) {
      setError(err.message || 'Erreur chargement utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patchUser = async (user, patch) => {
    const nextRole = patch.role || user.role;
    const nextMandataire = nextRole === 'partner'
      ? (patch.mandataire_code ?? user.mandataireCode)
      : undefined;
    if (nextRole === 'partner' && !nextMandataire) {
      setError('mandataire_code obligatoire pour un partenaire');
      return;
    }
    setSaving(user.id);
    setError('');
    try {
      await ceeApi.updateParametresUser(user.id, {
        role: nextRole,
        mandataire_code: nextMandataire,
      });
      await load();
    } catch (err) {
      setError(err.message || 'Erreur sauvegarde utilisateur');
    } finally {
      setSaving('');
    }
  };

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
      <h2 className="text-lg font-bold text-[#0f172a] mb-3">Utilisateurs campagne</h2>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 mb-3 text-sm">{error}</div>}
      {loading ? <p className="text-sm text-gray-500">Chargement...</p> : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-gray-500 border-b">
            <tr>
              <th className="py-2">Nom</th>
              <th>Email</th>
              <th>Role</th>
              <th>Mandataire</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b last:border-0">
                <td className="py-2 font-medium">{u.name}</td>
                <td className="text-gray-600">{u.email}</td>
                <td>
                  <select
                    value={u.role}
                    disabled={saving === u.id}
                    onChange={(e) => {
                      const role = e.target.value;
                      const fallbackMandataire = role === 'partner'
                        ? (u.mandataireCode || mandataires.find((m) => m.active)?.code || '')
                        : undefined;
                      patchUser(u, { role, mandataire_code: fallbackMandataire });
                    }}
                    className="border border-gray-200 rounded-md px-2 py-1.5 text-xs bg-white"
                  >
                    {ROLES.map((role) => (
                      <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                    ))}
                  </select>
                </td>
                <td>
                  {u.role === 'partner' ? (
                    <select
                      value={u.mandataireCode || ''}
                      disabled={saving === u.id}
                      onChange={(e) => patchUser(u, { mandataire_code: e.target.value })}
                      className="border border-gray-200 rounded-md px-2 py-1.5 text-xs bg-white"
                    >
                      <option value="" disabled>Mandataire requis</option>
                      {mandataires.map((m) => (
                        <option key={m.code} value={m.code} disabled={!m.active}>
                          {m.code}{m.active ? '' : ' (inactif)'}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan="4" className="text-center text-gray-400 py-3">Aucun utilisateur</td></tr>
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}
